import React from "react";
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import AwardIcon from "../../../components/Svgs/AwardIcon/AwardIcon";
import { alpha } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import AwardDetailView from "./AwardDetailView";
import LeadersList from "./LeadersList";
import useScopedTranslation from "../../../hooks/useScopedTranslation";

const COMPLETIONS_COUNT_KEY = "completions_count";

const useStyles = makeStyles(theme => ({
  list: {
    marginTop: theme.spacing(1)
  },
  listItem: {
    minHeight: theme.spacing(4),
    paddingTop: 0,
    paddingBottom: 0
  },
  listIcon: {
    minWidth: theme.spacing(2)
  }
}));

export default function AwardDetailViewModal({
  awardColor,
  awardType,
  awardTitle,
  customIcon,
  description,
  criteria,
  leaders,
  open,
  onClose
}) {
  const classes = useStyles();
  const t = useScopedTranslation("awards.criteria");
  const labelT = useScopedTranslation("awards.detailViewModal");
  const unitsT = useScopedTranslation("units");

  const formatTenure = message => {
    const unit = message.match(/\b(\w+)$/)[0];
    return message
      .replace(/At company for/, t("tenure"))
      .replace(unit, unitsT(`intervals.${unit}`));
  };

  const formatCompletionMessage = criteria => {
    const timeOrTimes =
      criteria.value > 1
        ? "completion_times_since_date"
        : "completion_time_since_date";
    return t(timeOrTimes, { ...criteria, units: "days" });
  };

  const otherCriteria = criteria => {
    return criteria.otherCriteriaRaw.map(cr => {
      if (cr.key == "tenure") return formatTenure(criteria.otherCriteria[0]);
      return formatCompletionMessage(cr);
    });
  };

  const completionCiteria = criteria.completedCourses.map(course => {
    const criteriaData = {
      courseTitle: course.courseTitle,
      criteria: []
    };

    const timesCriteria = course.criteriaRaw.find(
      cr => cr.key === COMPLETIONS_COUNT_KEY
    );
    const timesNumber = timesCriteria ? timesCriteria.value : 1;
    const unit = timesNumber > 1 ? "times_plural" : "times";
    const times = unitsT(`${unit}`, { count: timesNumber });
    const title = course.courseTitle;

    if (timesCriteria && course.criteriaRaw.length === 1) {
      criteriaData.criteria.push(t(timesCriteria.key, { times, title }));
    } else {
      course.criteriaRaw
        .filter(cr => cr.key !== COMPLETIONS_COUNT_KEY)
        .forEach(cr => {
          criteriaData.criteria.push(t(cr.key, { times, title, ...cr }));
        });
    }
    return criteriaData;
  });

  return (
    <AwardDetailView
      dialogContent={
        <Box display="flex" flexDirection={{ xs: "column", sm: "row" }}>
          <Box
            bgcolor={
              awardColor === "#000000"
                ? "rgba(0,0,0,0)"
                : alpha(`${awardColor}`, 0.1)
            }
            minHeight={"70vh"}
            width={{ xs: "unset", sm: "60%" }}
            padding={5}
          >
            <Box display="flex" mb={3}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                ml={-2.5}
                mr={3}
              >
                <AwardIcon
                  awardTitle={awardTitle}
                  customIcon={customIcon}
                  awardColor={awardColor}
                  awardType={awardType}
                  hasBackground={false}
                  iconSize="100%"
                  width={122}
                  height={122}
                />
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <Box mb={1}>
                  <Typography variant="h5">{awardTitle}</Typography>
                </Box>
                <Typography variant="body2" color="textSecondary">
                  {description}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Divider />
            </Box>
            <Box mt={2}>
              <Typography variant="subtitle1">{labelT("criteria")}</Typography>
              <List className={classes.list}>
                {otherCriteria(criteria).map((criteria, index) => (
                  <ListItem
                    className={classes.listItem}
                    key={"criteria" + index}
                  >
                    <ListItemIcon className={classes.listIcon}>
                      <Typography variant="h6" color="textSecondary">
                        &#8226;
                      </Typography>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" color="textSecondary">
                          {criteria}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
                {completionCiteria.length > 0 && (
                  <React.Fragment>
                    <ListItem
                      className={classes.listItem}
                      key={"criteria last"}
                    >
                      <ListItemIcon className={classes.listIcon}>
                        <Typography variant="h6" color="textSecondary">
                          &#8226;
                        </Typography>
                      </ListItemIcon>
                      <ListItemText
                        primary={labelT("trainingToComplete")}
                        primaryTypographyProps={{
                          color: "textSecondary"
                        }}
                      ></ListItemText>
                    </ListItem>
                    <ListItem className={classes.listItem}>
                      <ListItemText>
                        <List>
                          {completionCiteria.map((course, ind) => (
                            <ListItem
                              alignItems="flex-start"
                              className={classes.listItem}
                              key={"course" + ind}
                            >
                              <ListItemIcon className={classes.listIcon}>
                                <Typography variant="h6" color="textSecondary">
                                  &#8226;
                                </Typography>
                              </ListItemIcon>
                              <ListItemText
                                primary={course.courseTitle}
                                primaryTypographyProps={{
                                  color: "textSecondary"
                                }}
                                secondary={course.criteria.join(", ")}
                                secondaryTypographyProps={{
                                  color: "secondary.500"
                                }}
                              ></ListItemText>
                            </ListItem>
                          ))}
                        </List>
                      </ListItemText>
                    </ListItem>
                  </React.Fragment>
                )}
              </List>
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            pt={5}
            width={{ xs: "unset", sm: "40%" }}
          >
            <Box pl={5} pr={5}>
              <Typography variant="h5">{labelT("learnersAwarded")}</Typography>
            </Box>
            <Box display="flex" flexDirection="column" px={5}>
              <LeadersList leaders={leaders} />
            </Box>
          </Box>
        </Box>
      }
      open={open}
      onClose={onClose}
      showCloseIcon
      descriptionView
    />
  );
}
